import { useMemo } from 'react';
import type { TradingProfile } from '@einhaus/project314-types';
import { useTradingProfiles } from '@/hooks/useTradingProfiles';
import SearchSelect, { type SearchSelectFormProps, type SearchSelectItem } from './SearchSelect';

export interface TradingProfileSelectProps extends SearchSelectFormProps {
    value: number | null;
    onChange: (tradingProfileId: number | null) => void;
    /** Narrows the selectable list, e.g. to owned or shareable profiles */
    filter?: (tradingProfile: TradingProfile) => boolean;
}

const toItem = (tradingProfile: TradingProfile): SearchSelectItem => {
    const name = tradingProfile.name || `Trading Profile ${tradingProfile.id}`;

    return {
        label: `${name} (#${tradingProfile.id})`,
        name,
        value: tradingProfile.id
    };
};

const missingItem = (tradingProfileId: number): SearchSelectItem => ({
    label: `Trading Profile ${tradingProfileId} (#${tradingProfileId})`,
    meta: 'Not in your current trading profiles',
    name: `Trading profile #${tradingProfileId}`,
    value: tradingProfileId
});

/**
 * Searchable single select for the user's trading profiles. Fetches its own
 * data via useTradingProfiles, so call sites only manage the selected id.
 * Search matches name and id (with or without #).
 */
const TradingProfileSelect = (props: TradingProfileSelectProps) => {
    const { value, onChange, filter, placeholder, ...formProps } = props;

    const { error: profilesError, isLoading, tradingProfiles } = useTradingProfiles();

    const items = useMemo(() => {
        const visible = filter ? tradingProfiles.filter(filter) : tradingProfiles;

        return visible.map(toItem);
    }, [tradingProfiles, filter]);

    const loadError = profilesError ? `Failed to load trading profiles: ${profilesError.message}` : undefined;

    return (
        <SearchSelect
            {...formProps}
            isLoading={isLoading}
            items={items}
            loadError={loadError}
            mode="single"
            placeholder={placeholder ?? 'Select a trading profile...'}
            resolveMissingItem={missingItem}
            value={value}
            onChange={onChange}
        />
    );
};

export default TradingProfileSelect;
